import React from 'react';

const SUPPORT_EMAIL = 'imealapp@outlook.com';

/** Terms template aligned with ReRoomy structure; customise product details before App Store submission. */
export const iMealTermsExtended = {
  pageTitle: 'Terms of Use',
  subtitle: 'iMeal · East IT Services s.r.o.',
  updatedBadge: 'Last updated: May 6, 2026',
  tocTitle: 'Table of Contents',
  tocItems: [
    { id: 's1', label: 'Acceptance of Terms' },
    { id: 's2', label: 'Description of Service' },
    { id: 's3', label: 'License to Use' },
    { id: 's4', label: 'Subscriptions & Billing' },
    { id: 's5', label: 'User Content & Camera' },
    { id: 's6', label: 'AI-Generated Content' },
    { id: 's7', label: 'Prohibited Uses' },
    { id: 's8', label: 'Intellectual Property' },
    { id: 's9', label: 'Disclaimers' },
    { id: 's10', label: 'Limitation of Liability' },
    { id: 's11', label: 'Termination' },
    { id: 's12', label: 'Governing Law' },
  ],
  sections: [
    {
      id: 's1',
      num: '01',
      title: 'Acceptance of Terms',
      body: (
        <>
          <p>
            These Terms of Use (&quot;Terms&quot;) govern your access to and use of iMeal, a mobile application
            (&quot;App&quot;) developed by <strong>East IT Services s.r.o.</strong> (&quot;Company,&quot; &quot;we,&quot;
            &quot;us,&quot; or &quot;our&quot;).
          </p>
          <p>
            By downloading, installing, or using iMeal, you confirm that you are at least 13 years of age (or the
            applicable age of majority in your jurisdiction) and that you agree to be bound by these Terms. If you do
            not agree to these Terms, you must not use the App.
          </p>
          <p>
            We reserve the right to update these Terms at any time. Continued use of the App after changes are posted
            constitutes your acceptance of the revised Terms.
          </p>
        </>
      ),
    },
    {
      id: 's2',
      num: '02',
      title: 'Description of Service',
      body: (
        <>
          <p>
            iMeal is an application that helps you plan meals and explore food-related ideas. Depending on the
            version of the App, features may include:
          </p>
          <ul>
            <li>Capturing or uploading photos of food, ingredients, or related content</li>
            <li>
              Generating suggestions, meal ideas, or other outputs using Google&apos;s Gemini AI models (or other AI
              services we may integrate)
            </li>
            <li>Saving and managing content on your device</li>
          </ul>
          <p>
            The App is available on iOS devices through the Apple App Store. Certain features may require an active
            subscription. We reserve the right to modify, suspend, or discontinue any part of the service at any time
            with reasonable notice.
          </p>
        </>
      ),
    },
    {
      id: 's3',
      num: '03',
      title: 'License to Use',
      body: (
        <>
          <p>
            Subject to your compliance with these Terms, East IT Services s.r.o. grants you a limited, non-exclusive,
            non-transferable, revocable license to download and use iMeal on Apple devices you own or control, solely
            for your personal, non-commercial purposes.
          </p>
          <p>You may not:</p>
          <ul>
            <li>Copy, modify, or distribute the App or any part of it</li>
            <li>Reverse engineer, decompile, or disassemble the App</li>
            <li>Rent, lease, or sublicense the App to any third party</li>
            <li>Remove any proprietary notices or labels on the App</li>
            <li>Use the App for any commercial purpose without our prior written consent</li>
          </ul>
        </>
      ),
    },
    {
      id: 's4',
      num: '04',
      title: 'Subscriptions & Billing',
      body: (
        <>
          <p>
            iMeal may offer auto-renewable subscriptions that unlock premium features. By purchasing a subscription,
            you agree to the following terms:
          </p>
          <h3>Billing</h3>
          <ul>
            <li>
              Subscriptions are billed through your Apple ID on a recurring basis (weekly, monthly, or annual,
              depending on the plan selected)
            </li>
            <li>Payment is charged to your Apple ID account at confirmation of purchase</li>
            <li>
              Subscriptions automatically renew unless cancelled at least 24 hours before the end of the current period
            </li>
          </ul>
          <h3>Cancellation</h3>
          <ul>
            <li>
              You may cancel your subscription at any time through your Apple ID settings: Settings → [Your Name] →
              Subscriptions
            </li>
            <li>
              Cancellation takes effect at the end of the current billing period; you will retain access until that date
            </li>
            <li>
              We do not offer refunds for partial subscription periods, except as required by applicable law
            </li>
          </ul>
          <h3>Refunds</h3>
          <p>
            All refund requests must be submitted directly to Apple in accordance with their refund policy. We do not
            process refunds independently.
          </p>
          <div className="app-legal-doc__highlight">
            <strong>Free Trial:</strong> If we offer a free trial, it will be clearly disclosed before purchase. Unused free
            trial time will be forfeited upon purchasing a subscription.
          </div>
        </>
      ),
    },
    {
      id: 's5',
      num: '05',
      title: 'User Content & Camera',
      body: (
        <>
          <p>
            You may submit photos or other inputs (&quot;User Content&quot;) to use features of the App. By submitting User
            Content, you represent and warrant that:
          </p>
          <ul>
            <li>You own the content or have the necessary rights to use it</li>
            <li>The content does not violate any applicable law or third-party rights</li>
            <li>You will not submit imagery of identifiable people without their consent where required</li>
            <li>
              You will not submit content containing sensitive personal information (e.g., documents, payment details, or
              screens with passwords)
            </li>
          </ul>
          <p>
            You retain all ownership rights in your User Content. By using the App, you grant us and our AI service
            providers (such as Google) a limited licence to process your inputs solely to provide the features you request.
          </p>
          <div className="app-legal-doc__warning">
            <strong>Important:</strong> Do not submit content you do not have the right to share, or imagery of minors in
            any inappropriate context.
          </div>
        </>
      ),
    },
    {
      id: 's6',
      num: '06',
      title: 'AI-Generated Content',
      body: (
        <>
          <p>
            The App may use Google&apos;s Gemini AI models (or other models) to generate text, images, or other outputs.
            Regarding AI-generated content:
          </p>
          <ul>
            <li>Outputs are produced automatically and may not be accurate, complete, or suitable for your needs</li>
            <li>
              Suggestions are for personal, informational use only — they are not medical, nutritional, or dietary
              advice from a licensed professional
            </li>
            <li>We do not guarantee the accuracy, quality, or fitness for any purpose of AI-generated content</li>
            <li>You use AI-generated content at your own risk</li>
          </ul>
          <p>
            To the extent permitted by law, AI-generated content is provided for your personal use. We make no claim of
            ownership over outputs generated from your User Content.
          </p>
        </>
      ),
    },
    {
      id: 's7',
      num: '07',
      title: 'Prohibited Uses',
      body: (
        <>
          <p>You agree not to use iMeal to:</p>
          <ul>
            <li>Violate any applicable law or regulation</li>
            <li>Submit or process content of minors in any inappropriate context</li>
            <li>Generate content that is offensive, harmful, defamatory, or infringes third-party rights</li>
            <li>Attempt to circumvent or disable any security features of the App</li>
            <li>Use automated tools, bots, or scripts to interact with the App</li>
            <li>Resell, redistribute, or commercially exploit outputs without our express permission</li>
            <li>Interfere with the proper working of the App or its servers</li>
          </ul>
          <p>We reserve the right to terminate your access if you violate these prohibited use provisions.</p>
        </>
      ),
    },
    {
      id: 's8',
      num: '08',
      title: 'Intellectual Property',
      body: (
        <>
          <p>
            All rights, title, and interest in and to iMeal — including its design, graphics, code, trademarks, and other
            content — are owned by East IT Services s.r.o. or our licensors. Nothing in these Terms grants you ownership
            rights in the App.
          </p>
          <p>
            The &quot;iMeal&quot; name and logo are trademarks of East IT Services s.r.o. You may not use our trademarks
            without our prior written consent.
          </p>
          <p>Google Gemini is a product of Google LLC and is subject to Google&apos;s own terms and policies.</p>
        </>
      ),
    },
    {
      id: 's9',
      num: '09',
      title: 'Disclaimers',
      body: (
        <>
          <p>
            iMeal is provided on an &quot;as is&quot; and &quot;as available&quot; basis without warranties of any kind,
            whether express or implied, including but not limited to warranties of merchantability, fitness for a
            particular purpose, or non-infringement.
          </p>
          <ul>
            <li>We do not warrant that the App will be uninterrupted, error-free, or free of viruses</li>
            <li>AI-generated results may vary and are not guaranteed to be accurate or satisfactory</li>
            <li>We are not responsible for the availability or actions of third-party services (e.g., Google Gemini, Apple)</li>
          </ul>
          <p>Some jurisdictions do not allow the exclusion of implied warranties, so some of the above may not apply to you.</p>
        </>
      ),
    },
    {
      id: 's10',
      num: '10',
      title: 'Limitation of Liability',
      body: (
        <>
          <p>
            To the fullest extent permitted by applicable law, East IT Services s.r.o. and its officers, directors,
            employees, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive
            damages, including loss of data, loss of profits, or loss of goodwill, arising from your use of the App.
          </p>
          <p>
            In no event shall our total liability to you exceed the amount you paid for the App or subscription in the
            twelve (12) months preceding the claim.
          </p>
        </>
      ),
    },
    {
      id: 's11',
      num: '11',
      title: 'Termination',
      body: (
        <>
          <p>
            These Terms are effective until terminated. Your rights under these Terms will terminate automatically without
            notice if you fail to comply with any of its provisions.
          </p>
          <p>
            We reserve the right to suspend or terminate your access to the App at our sole discretion, with or without
            cause, and without liability to you. Upon termination, you must cease all use of the App and delete it from
            your device.
          </p>
          <p>
            Cancelling your subscription does not delete locally stored data — you retain access to files saved on your
            device.
          </p>
        </>
      ),
    },
    {
      id: 's12',
      num: '12',
      title: 'Governing Law',
      body: (
        <>
          <p>
            These Terms shall be governed by and construed in accordance with applicable European Union law, without regard
            to its conflict of law principles.
          </p>
          <p>
            Any disputes arising from these Terms or the use of iMeal shall be subject to the exclusive jurisdiction of
            the courts of the Slovak Republic, unless applicable consumer protection law in your country requires otherwise.
          </p>
          <p>
            If you have questions or concerns about these Terms, please contact us at{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        </>
      ),
    },
  ],
};
